import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import React from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "../theme/useTheme";

import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";

import Popover from "@mui/material/Popover";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";

// Custom column selector component
export const CustomColumnSelector = ({ apiRef }: { apiRef: any }) => {
  const themeCtx = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [columns, setColumns] = React.useState<any[]>([]);
  const [visibility, setVisibility] = React.useState<Record<string, boolean>>(
    {}
  );

  // Sync columns whenever popover is opened
  React.useEffect(() => {
    if (anchorEl && apiRef.current) {
      setTimeout(() => {
        const cols = apiRef.current
          .getAllColumns()
          .filter((col: any) => col.field !== "__check__");
        setColumns(cols);
        // Build visibility state from columns
        const vis: Record<string, boolean> = {};
        cols.forEach((col: any) => {
          vis[col.field] = !col.hide;
        });
        setVisibility(vis);
      }, 200);
    }
  }, [anchorEl, apiRef]);

  const handleToggle = (field: string, checked: boolean) => {
    apiRef.current.setColumnVisibility(field, checked);
    setVisibility((prev) => ({ ...prev, [field]: checked }));
  };

  const handleShowAll = () => {
    columns.forEach((col) =>
      apiRef.current.setColumnVisibility(col.field, true)
    );
    // Set all to true
    setVisibility((prev) => {
      const updated: Record<string, boolean> = { ...prev };
      columns.forEach((col) => {
        updated[col.field] = true;
      });
      return updated;
    });
  };
  const handleHideAll = () => {
    columns.forEach((col) =>
      apiRef.current.setColumnVisibility(col.field, false)
    );
    // Set all to false
    setVisibility((prev) => {
      const updated: Record<string, boolean> = { ...prev };
      columns.forEach((col) => {
        updated[col.field] = false;
      });
      return updated;
    });
  };

  return (
    <>
      <Tooltip title="Show/Hide Columns">
        <IconButton
          size="small"
          aria-label="show-hide-columns"
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
          }}
          sx={{ color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827" }}
        >
          <ViewColumnIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            backgroundColor: themeCtx.mode === "dark" ? "#1f2937" : "#fff",
            color: themeCtx.mode === "dark" ? "#e5e7eb" : "#000",
          },
        }}
      >
        <div style={{ padding: 16, minWidth: 220 }}>
          <div
            style={{
              fontWeight: 500,
              marginBottom: 8,
              color: themeCtx.mode === "dark" ? "#f3f4f6" : "#111827",
            }}
          >
            Find column
          </div>
          {columns.map((col) => (
            <div
              key={col.field}
              style={{ display: "flex", alignItems: "center", marginBottom: 4 }}
            >
              <Switch
                checked={visibility[col.field]}
                onChange={(_, checked) => handleToggle(col.field, checked)}
                size="small"
              />
              <span
                style={{
                  marginLeft: 8,
                  color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
                }}
              >
                {col.headerName}
              </span>
            </div>
          ))}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 12,
            }}
          >
            <Button size="small" onClick={handleHideAll}>
              Hide all
            </Button>
            <Button size="small" onClick={handleShowAll}>
              Show all
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export interface DataGridToolbarProps {
  apiRef: any;
  fullscreen: boolean;
  setFullscreen: (v: boolean) => void;
  searchActive: boolean;
  setSearchActive: (v: boolean) => void;
  searchValue: string;
  setSearchValue: (v: string) => void;
}

export const DataGridToolbar: React.FC<DataGridToolbarProps> = ({
  apiRef,
  fullscreen,
  setFullscreen,
  searchActive,
  setSearchActive,
  searchValue,
  setSearchValue,
}) => {
  const themeCtx = useTheme();

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        width: "100%",
        gap: "0.5rem",
        paddingRight: "0.5rem",
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem",
      }}
    >
      <CustomColumnSelector apiRef={apiRef} />
      <span style={{ color: themeCtx.mode === "dark" ? "#6b7280" : "#ccc" }}>
        |
      </span>
      <Tooltip title="Export as CSV">
        <IconButton
          size="small"
          aria-label="export"
          onClick={() => apiRef.current.exportDataAsCsv()}
          sx={{ color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827" }}
        >
          <FileDownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <span style={{ color: themeCtx.mode === "dark" ? "#6b7280" : "#ccc" }}>
        |
      </span>
      {/* Search icon and box */}
      {!searchActive ? (
        <Tooltip title="Search">
          <IconButton
            size="small"
            aria-label="search"
            onClick={() => setSearchActive(true)}
            sx={{ color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827" }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            border:
              themeCtx.mode === "dark"
                ? "1px solid #4b5563"
                : "1px solid #d1d5db",
            borderRadius: 6,
            padding: "2px 8px",
            background: themeCtx.mode === "dark" ? "#374151" : "#fff",
            minWidth: 180,
            width: 180,
          }}
        >
          <SearchIcon
            fontSize="small"
            style={{ color: themeCtx.mode === "dark" ? "#9ca3af" : "#888" }}
          />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search..."
            style={{
              border: "none",
              outline: "none",
              fontSize: 16,
              marginLeft: 6,
              background: "transparent",
              width: 120,
              transition: "width 0.1s",
              color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
            }}
            autoFocus
            onBlur={() => {
              if (!searchValue) setSearchActive(false);
            }}
          />
          <div
            style={{
              width: 22,
              height: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {searchValue && (
              <IconButton
                size="small"
                aria-label="clear-search"
                onClick={() => setSearchValue("")}
                style={{ marginLeft: 2, padding: 2, width: 20, height: 20 }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 6L12 12M12 6L6 12"
                    stroke={themeCtx.mode === "dark" ? "#9ca3af" : "#888"}
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </IconButton>
            )}
          </div>
        </div>
      )}
      <span style={{ color: themeCtx.mode === "dark" ? "#6b7280" : "#ccc" }}>
        |
      </span>
      <Tooltip title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}>
        <IconButton
          size="small"
          aria-label="fullscreen"
          onClick={() => setFullscreen(!fullscreen)}
          sx={{ color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827" }}
        >
          {fullscreen ? (
            <FullscreenExitIcon fontSize="small" />
          ) : (
            <FullscreenIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </div>
  );
};
